import { FC, ReactNode, useRef } from 'react';
import { RootState } from 'store';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { Checkbox } from 'shared/components/Checkbox';
import { Spacer, WhiteBox } from 'shared/components/Layout';
import { useScrollToElement } from 'shared/hooks/useScrollToElement';

import { ACKTestCheckboxEnum } from 'tests/enums/ACKTestEnums';
import { testId } from 'tests/utils';

import { AckConsentForm } from '../Acknowledgment';
import { ACKStatusEnum } from '../enums/ACKStatusEnum';
import { SpecialOrderApproval } from '../interface/SpecialOrderApproval';
import { CoverSheetNoteItem } from './CoverSheetNoteItem';

interface CoverSheetProps {
  consentTitle?: string;
  consentNeeded?: boolean;
  isSpecialOrderApproval?: boolean;
  specialOrderApproval?: SpecialOrderApproval;
  footer?: ReactNode;
}

const CoverSheetContainer = styled(WhiteBox)``;

const CoverSheetBody = styled.div``;

const CoverSheetConsent = styled.div<{ isInvalid: boolean }>`
  ${({ isInvalid }) =>
    isInvalid &&
    css`
      position: relative;
      box-shadow: 0 0 0 15px ${({ theme }) => theme.wildSandTwo};
      border-radius: 3px;
      background-color: ${({ theme }) => theme.wildSandTwo};
    `}
`;

const CoverSheet: FC<CoverSheetProps> = ({
  children,
  consentTitle,
  consentNeeded,
  isSpecialOrderApproval,
  specialOrderApproval,
  footer,
}) => {
  const methodsContext = useFormContext<AckConsentForm>();

  const converSheetRef = useRef<HTMLDivElement | null>(null);

  const latestACK = useSelector(
    (state: RootState) => state.ACKReducer.latestACK
  );

  const isCurrentUserViewer = useSelector(
    (state: RootState) => state.orderReducer.isCurrentUserViewer
  );

  const previousVersionRef = useRef<number | null>(null);
  const preservedNoteRef = useRef<string | null>(null);

  const preservedNote = (() => {
    if (isSpecialOrderApproval || !latestACK) {
      return null;
    }

    const currentVersion = latestACK.version;
    const previousVersion = previousVersionRef.current;

    if (previousVersion === null || currentVersion !== previousVersion) {
      const newNote = latestACK.note;
      previousVersionRef.current = currentVersion;
      preservedNoteRef.current = newNote;
      return newNote;
    }

    if (latestACK.note) {
      preservedNoteRef.current = latestACK.note;
      return latestACK.note;
    }

    return preservedNoteRef.current;
  })();

  const ackFieldError = !isSpecialOrderApproval
    ? methodsContext.formState.errors.consentGiven
    : undefined;

  useScrollToElement({
    errors: methodsContext.formState.errors,
    error: ackFieldError,
    ref: converSheetRef,
    fieldName: 'consentGiven',
  });

  const hasActiveVersionForACK = !!(
    latestACK &&
    latestACK.status !== undefined &&
    latestACK.status === ACKStatusEnum.PENDING
  );

  return (
    <CoverSheetContainer ref={converSheetRef} padding="25px">
      <CoverSheetBody>{children}</CoverSheetBody>

      {isSpecialOrderApproval && specialOrderApproval && (
        <CoverSheetNoteItem note={specialOrderApproval.note} />
      )}

      {footer}

      {!isSpecialOrderApproval &&
        latestACK &&
        latestACK.previousNotes &&
        latestACK.previousNotes.length > 0 && (
          <>
            {latestACK.previousNotes.map((noteHistory, index) => (
              <>
                <CoverSheetNoteItem
                  key={`previous-note-${noteHistory.version}`}
                  version={noteHistory.version}
                  note={noteHistory.note}
                />
                {index < latestACK.previousNotes.length - 1 && (
                  <Spacer h="20px" />
                )}
              </>
            ))}

            <Spacer h="20px" />
          </>
        )}

      {!isSpecialOrderApproval && latestACK && preservedNote && (
        <>
          <CoverSheetNoteItem
            version={latestACK.version}
            note={preservedNote}
          />
        </>
      )}

      {!isSpecialOrderApproval &&
        consentNeeded !== false &&
        hasActiveVersionForACK && (
          <>
            <Spacer h="30px" />

            <CoverSheetConsent
              isInvalid={!!methodsContext.formState.errors.consentGiven}
            >
              <Checkbox
                {...methodsContext.register('consentGiven', {
                  required: latestACK?.status !== ACKStatusEnum.APPROVED,
                })}
                disabled={
                  latestACK?.status === ACKStatusEnum.APPROVED ||
                  isCurrentUserViewer
                }
                title={
                  consentTitle ??
                  'I agree that this acknowledgment is approved.'
                }
                id="ackConsentGiven"
                {...testId(ACKTestCheckboxEnum.ACK_CONSENT_CHECKBOX)}
                aria-invalid={
                  methodsContext.formState.errors.consentGiven
                    ? 'true'
                    : 'false'
                }
              />
            </CoverSheetConsent>
          </>
        )}
    </CoverSheetContainer>
  );
};

CoverSheet.defaultProps = {
  consentNeeded: true,
  isSpecialOrderApproval: false,
};

export default CoverSheet;
